export type Vector = { x: number; y: number; z: number };

export function hexToVector(color: string): Vector {
  const r = parseInt(`${color[1]}${color[2]}`, 16);
  const g = parseInt(`${color[3]}${color[4]}`, 16);
  const b = parseInt(`${color[5]}${color[6]}`, 16);
  return {
    x: r,
    y: g,
    z: b,
  };
}

const distance = (v: Vector, w: Vector): number =>
  Math.sqrt((v.x - w.x) ** 2 + (v.y - w.y) ** 2 + (v.z - w.z) ** 2);

export function findClosestColorIndexFromList(
  color: string,
  colorList: string[],
): number | undefined {
  if (colorList.length === 0) {
    return undefined;
  }

  const colorVector = hexToVector(color);
  const colorListVectors = colorList.map(c => hexToVector(c));

  let closestColorIndex = 0;
  let closestDistance = distance(colorVector, colorListVectors[0]);

  colorListVectors.forEach((vector, index) => {
    const d = distance(colorVector, vector);
    if (d <= closestDistance) {
      closestDistance = d;
      closestColorIndex = index;
    }
  });

  return closestColorIndex;
}

// pulled from https://gist.github.com/xenozauros/f6e185c8de2a04cdfecf
const HexToHSL = (hex: string): { h: number; s: number; l: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
    hex,
  ) as string[];
  let r = parseInt(result[1], 16);
  let g = parseInt(result[2], 16);
  let b = parseInt(result[3], 16);
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s;
  const l = (max + min) / 2;
  if (max === min) {
    h = 0; // achromatic
    s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
    }
    h /= 6;
  }
  return { h, s, l };
};

const whiteThreshold = 0.99;
const blackThreshold = 0.01;

export function findClosestHueColorIndexFromList(
  color: string,
  colorList: string[],
): number | undefined {
  if (colorList.length === 0) {
    return undefined;
  }

  const colorHSL = HexToHSL(color);
  const colorListHSLs = colorList.map(c => HexToHSL(c));

  let closestColorIndex = 0;
  let closestDistance = Math.abs(colorHSL.h - colorListHSLs[0].h);

  const black = colorListHSLs.findIndex(c => c.l < blackThreshold);
  const white = colorListHSLs.findIndex(c => c.l > whiteThreshold);

  if (black !== -1 && colorHSL.l < blackThreshold) {
    return black;
  }

  if (white !== -1 && colorHSL.l > whiteThreshold) {
    return white;
  }

  colorListHSLs.forEach((c, index) => {
    const d = Math.abs(colorHSL.h - c.h);
    if (d <= closestDistance) {
      closestDistance = d;
      closestColorIndex = index;
    }
  });

  return closestColorIndex;
}
